import { getContrastRatio } from "@mui/material/styles";

// Marca blanca por agencia: deriva la paleta completa a partir de un color
// primario y uno secundario y la aplica sobre los objetos de color del theme.

// Valores de marca por defecto; theme.js los usa y applyBranding los sustituye
// dentro de cualquier string de color (hex sueltos, gradientes, sombras rgba).
export const DEFAULT_TOKENS = {
  pink: "#E6007E",
  pinkDark: "#C40069",
  coral: "#F2596A",
  orange: "#F2940D",
  soft: "#FDEBF4",
  softBorder: "#F7D9E9",
  rgb: "230, 0, 126",
};
export const DEFAULT_PRIMARY = DEFAULT_TOKENS.pink;
export const DEFAULT_SECONDARY = DEFAULT_TOKENS.orange;
export const DEFAULT_INK = "#0F172A";

const HEX_RE = /^#[0-9a-f]{6}$/i;

export const isHexColor = (value) => typeof value === "string" && HEX_RE.test(value);

const toRgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));

const toHex = (rgb) =>
  `#${rgb.map((c) => Math.round(Math.min(255, Math.max(0, c))).toString(16).padStart(2, "0")).join("")}`.toUpperCase();

// Mezcla `hex` con `other` en proporción `amount` (0 = hex, 1 = other)
const mix = (hex, other, amount) => {
  const a = toRgb(hex);
  const b = toRgb(other);
  return toHex(a.map((c, i) => c + (b[i] - c) * amount));
};

const tint = (hex, amount) => mix(hex, "#FFFFFF", amount);
const shade = (hex, amount) => mix(hex, "#000000", amount);

export const contrastWithWhite = (hex) => getContrastRatio(hex, "#FFFFFF");

// Texto blanco u oscuro, el que se lea mejor sobre `hex`
const textOn = (hex) => (contrastWithWhite(hex) >= 3 ? "#FFFFFF" : DEFAULT_INK);

// Oscurece el color lo justo para que el texto blanco encima sea legible
const readableOnWhite = (hex, minContrast = 4.5) => {
  let color = hex;
  for (let i = 0; i < 20 && contrastWithWhite(color) < minContrast; i += 1) {
    color = shade(color, 0.08);
  }
  return color;
};

const rgbTriplet = (hex) => toRgb(hex).join(", ");

/**
 * Paleta derivada de la marca de una agencia. Devuelve null si no hay personalización.
 * - Primario: botones, avatar, menú lateral (item activo), acentos.
 * - Secundario: fondos de hover/activos, gráficas y botones oscuros (tal cual, con texto legible encima).
 */
export const deriveBrandPalette = ({ primary_color: primary, secondary_color: secondary } = {}) => {
  const customPrimary = isHexColor(primary) ? primary.toUpperCase() : null;
  const customSecondary = isHexColor(secondary) ? secondary.toUpperCase() : null;
  if (!customPrimary && !customSecondary) return null;

  const p = customPrimary || DEFAULT_PRIMARY;
  const s = customSecondary || DEFAULT_SECONDARY;
  // Sin secundario propio, los tonos suaves salen del primario para no mezclar marcas
  const softSource = customSecondary || p;
  // El secundario se usa tal cual como fondo; como texto sobre blanco, oscurecido hasta ser legible
  const inkFill = customSecondary || DEFAULT_INK;

  return {
    tokens: {
      pink: p,
      pinkDark: shade(p, 0.15),
      coral: tint(p, 0.3),
      orange: s,
      soft: tint(softSource, 0.9),
      softBorder: tint(softSource, 0.8),
      rgb: rgbTriplet(p),
    },
    ink: customSecondary ? readableOnWhite(customSecondary) : DEFAULT_INK,
    inkFill,
    inkFillText: textOn(inkFill),
    inkHover: customSecondary ? shade(inkFill, 0.12) : "#1E293B",
    orangeText: textOn(s),
    avatar: customPrimary || DEFAULT_INK,
    mediaSurface: tint(p, 0.92),
    mediaBorder: tint(p, 0.6),
    primaryContrastText: textOn(p),
  };
};

const escapeRe = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const buildReplacer = (palette) => {
  if (!palette) return (value) => value;
  // Una sola pasada: un color nuevo nunca se vuelve a sustituir aunque coincida con otro default
  const lookup = new Map(
    Object.entries(DEFAULT_TOKENS).map(([key, from]) => [from.toUpperCase(), palette.tokens[key]])
  );
  const re = new RegExp(Object.values(DEFAULT_TOKENS).map(escapeRe).join("|"), "gi");
  return (value) => value.replace(re, (match) => lookup.get(match.toUpperCase()));
};

/**
 * Guarda los strings originales de un árbol de objetos de color para poder
 * re-aplicar marcas sucesivas siempre desde los valores por defecto.
 */
export const snapshotColorTree = (roots) => {
  const originals = new Map();
  const visit = (obj) => {
    if (!obj || typeof obj !== "object" || originals.has(obj)) return;
    const strings = {};
    originals.set(obj, strings);
    Object.entries(obj).forEach(([key, value]) => {
      if (typeof value === "string") strings[key] = value;
      else visit(value);
    });
  };
  roots.forEach(visit);
  return originals;
};

export const recolorTree = (originals, replace) => {
  originals.forEach((strings, obj) => {
    Object.entries(strings).forEach(([key, value]) => {
      obj[key] = replace(value);
    });
  });
};

export const brandingKey = (branding) =>
  `${branding?.primary_color || ""}|${branding?.secondary_color || ""}`;
